
import {
    Box, Container, createStyles, rem, Group,
    Center, Text, Image, Title, Space, SimpleGrid, Tabs, Card, Button,
    Divider, Badge
} from '@mantine/core'
import '../App.css'
import transparentlogo from '../cs_frogtop_transparent_cropped.png'
import twophone from '../Mobile_smartphone_two_dark.png'
import laptop from '../laptop_final.png'
import apple from '../Download_on_the_App_Store_Badge_US-UK_RGB_wht_092917.svg';
import android from '../google-play-badge.png';
import Footer from '../components/Footer'
import Head from '../components/Head'
import TestimonialsCarousel from '../components/TestimonialsCarousel'

const useStyles = createStyles((theme) => ({

    // ── Hero ──────────────────────────────────────────────────────
    heroWrapper: {
        background: 'radial-gradient(ellipse at 60% 40%, #c8eaf5 0%, #e8f6fb 45%, #f4fbff 100%)',
        paddingBottom: rem(40),
    },

    hero: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing.xl,
        padding: `${rem(20)} ${rem(25)}`,

        [theme.fn.smallerThan('md')]: {
            flexDirection: 'column-reverse',
            padding: `${rem(10)} ${rem(16)}`,
        },
    },

    heroImageContainer: {
        flex: '0 0 33%',
        maxWidth: '33%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: theme.spacing.xl,

        [theme.fn.smallerThan('md')]: {
            flex: '0 0 60%',
            maxWidth: '60%',
            flexDirection: 'row',
        },

        [theme.fn.smallerThan('sm')]: {
            flex: '0 0 80%',
            maxWidth: '80%',
            flexDirection: 'column',
        },
    },

    heroImage: {
        width: '100%',
    },

    heroContent: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',

        [theme.fn.smallerThan('md')]: {
            width: '100%',
        },
    },

    heroTitle: {
        textAlign: 'center',
        color: '#7b00b8',
        fontSize: rem(52),
        lineHeight: 1.15,
        fontFamily: "'Merriweather', 'Georgia', serif",
        fontWeight: 900,
        letterSpacing: '-0.01em',

        [theme.fn.smallerThan('lg')]: {
            fontSize: rem(42),
        },

        [theme.fn.smallerThan('md')]: {
            fontSize: rem(34),
        },

        [theme.fn.smallerThan('sm')]: {
            fontSize: rem(28),
        },
    },

    // ── App-store badges ──────────────────────────────────────────
    badgeRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        gap: theme.spacing.sm,
        flexWrap: 'wrap',
        marginTop: rem(8),
    },

    badgeImg: {
        maxWidth: rem(180),

        [theme.fn.smallerThan('sm')]: {
            maxWidth: rem(140),
        },
    },

    // ── Dividers ──────────────────────────────────────────────────
    sectionDivider: {
        margin: `${rem(40)} 0 ${rem(32)}`,
    },

    dividerLabel: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 700,
        fontSize: rem(13),
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: theme.colors.gray[5],
    },

    // ── Feature tabs ──────────────────────────────────────────────
    tabsBox: {
        padding: rem(50),

        [theme.fn.smallerThan('sm')]: {
            padding: rem(20),
        },
    },

    featureCard: {
        background: 'rgba(255,255,255,0.7)',
        backdropFilter: 'blur(6px)',
        border: `1px solid ${theme.colors.blue[1]}`,
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',

        '&:hover': {
            boxShadow: theme.shadows.md,
            transform: 'translateY(-3px)',
        },
    },

    link: {
        display: 'block',
        lineHeight: 1,
        padding: `${rem(8)} ${rem(12)}`,
        borderRadius: theme.radius.sm,
        textDecoration: 'none',
        color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.gray[7],
        fontSize: theme.fontSizes.sm,
        fontWeight: 500,

        '&:hover': {
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
        }
    },
}));


function Landing() {

    const { classes } = useStyles()

    const links = [
        { 'link': '/About', 'label': 'About' },
        { 'link': '/Support', 'label': 'Support' },
        { 'link': '/Pp', 'label': 'Privacy' },
        { 'link': '/Tos', 'label': 'Terms of Use' },
        { 'link': '/Changelog', 'label': 'Changelog' },
    ]

    const fan_cards = [
        { title: 'Discover and support the next top comics at the ground level', content: 'Every year we reward the top comics on the app both globally and locally.  Nothing affects these rankings more than after show favoriting from fans and supporters!' },
        { title: 'Find live stand up that matches your personal sense of humor', content: 'Everyone has a different sense of humor.  Build your sense of humor profile by ranking your favorite comedians and see how you match up with comedians/friends/shows around you' },
        { title: 'Win prizes', content: 'Just like we have Top Comic awards we have Top Fan awards as well for you to win!' },
        //{ title: 'Collect badges as you explore the live stand up scene', content: "Pick up badges by checking in at open mics, festivals, and showcases.  You'll prove that you're a real live stand up comedy enthusiast!  You don't just 'like' videos on instagram, you show up and have a good time!" },
        { title: "Keep track of comedians you've seen", content: "Ever go to a show, have a great time but not even remember the name of your favorite comic that night?  In Comedy Seen, you keep track of all the comics you've seen automatically." },
        { title: 'Buy tickets to local stand up shows without paying exorbitant fees', content: "Other ticketing services charge way too much for what they offer." }
    ]

    const comedian_cards = [
        { title: 'Crowd sourced open mic map', content: 'Find open mics on our crowd sourced open mic map.  Contribute to the map and keep it current to earn points that move you up the leaderboard.' },
        { title: 'No mysterious algorithm.  Get exposure on the app by just doing stand up comedy', content: "No need to become a videographer to make reels or becoming a social media master.  Just get exposure by being a good comedian." },
        { title: 'Attract stand up comedy fans who have similar senses of humor as you', content: 'Comedy Seen facilitates fans who want to find stand up comedy shows featuring comics who have the same sense of humor as them.' },
        { title: 'Get rewarded with $$$', content: "At the end of the year if you place globally or in locally sponsored leaderboards for up and coming comics, you'll get cold hard cash!  No fee to participate!" },
        //{ title: 'Collect badges throughout your comedy journey', content: 'Badges are a fun thing to pick up at shows and a cool way to commemorate your career.' },

    ]

    const producer_cards = [
        { title: 'Lowest fees in the business', content: 'Our fees are the lowest in the business.  Check out our Pricing page.  The price is our unenshittifiable price.  Whatever price you lock in will be yours forever.' },
        { title: 'Fill rooms with more laughter', content: 'Fans who find your shows on Comedy Seen are more likely to enjoy your show because their senses of humor match.' },
        { title: 'Get discovered by hardcore stand up comedy fans', content: 'Comedy Seen is for the fans of LIVE stand up comedy who actually go to shows and not just like your posts.' },
        //{ title: 'Draw fans to your shows with badges', content: 'Whether you run a open mic, showcase, or a festival you can have badges that fans can pick up so they can prove their elite fan status.' },
    ]

    const testimonial_data = [
        {
            quote: 'Comedy Seen (formerly Pobblebonk) is a light at the end of a dark tunnel for anyone starting comedy.  The founder, Pete, built the app from the ground up and regularly talks to comics to ensure bugs are fixed and the user experience is imporoving at all times.  One of the best parts for me was winning the yearly comic awards.  Making money from comedy being only 2 years in was a great morale boost and helped motivate me to keep going.  I recommend the app for anyone trying to make it as a standup or has an appreciation for independently produced shows',
            author: 'Alex Shklar, NYC, Comedian and Producer',
            avatar: 'https://res.cloudinary.com/hwldcqspa/image/upload/c_fill,h_200,w_200/v1671154942/profile_pics/e2c48656-7ce2-11ed-b5c0-e2413904b2fa_5P3A0235.JPG.jpg',
            link: 'https://www.comedyseen.com/userprofile/205'
        },
        {
            quote: 'Comedy Seen provided a streamlined platform that allowed us to sell tickets to our audience without having to worry about raising prices to accomodate higher fees.  We were able to sell tickets for the right price and invest back into the show.',
            author: 'Max Kaczor, NYC, Comedian and Producer',
            avatar: 'https://res.cloudinary.com/hwldcqspa/image/upload/c_fill,h_200,w_200/v1689107610/profile_pics/3253e5a0-202a-11ee-a0f7-1a25c0094e5c_Image.jpeg.jpg',
            link: 'https://www.comedyseen.com/userprofile/3015'
        },
        {
            quote: 'I joined the app I can’t get enough of ComedySeen this app is AMAZING! Not only does it have lists of Comedy Shows, Open Mics but they also have tons of great tickets and rewards for fans. 10/10 Best App.  Reigning champion of Smackdown, WWE and Is it Cake or Not I reckon it’s Cake but it you don’t download this app I bet this shoe ain’t cake lol 😆',
            author: 'Alexander Fraser, Jacksonville, Comedian',
            avatar: 'https://res.cloudinary.com/hwldcqspa/image/upload/c_fill,h_200,w_200/v1762352732/profile_pics/4933dd82-ba53-11f0-b124-0e95cb5a9a5b_IMG_6513.png',
            link: 'https://www.comedyseen.com/userprofile/9505'

        },
        {
            quote:'Comedy Seen has been a great way for my open mic (now retired) to help local and out of town comics get time and be seen. I’ve been using Comedy Seen from the beginning of their start. Love the changes and updates they’ve made since then. Even better is their contests! I definitely recommend utilizing this app, whether you’re a Host, Booker, or just Business Owner trying to get more talent/customers to your location. Can’t wait to see what’s next in the future iterations of the app! 🤍🤍🤍',
            author: 'Vanessa Scaduto, Jacksonville/Austin, Comedian (Kill Tony ep. 763!) and Producer',
            avatar: 'https://res.cloudinary.com/hwldcqspa/image/upload/v1670900534/profile_pics/8b7ebf26-7a92-11ed-b05a-d2ec064c8afd_642D91CC-1019-464B-A2F4-A259ABE43388.jpeg.jpg',
            link:  'https://www.comedyseen.com/userprofile/1785'
        }
    ]

    const renderCards = (cards: { title: string; content: string }[]) => (
        <SimpleGrid
            cols={2}
            spacing="xl"
            breakpoints={[
                { maxWidth: '62rem', cols: 2, spacing: 'sm' },
                { maxWidth: '36rem', cols: 1, spacing: 'sm' }
            ]}
        >
            {cards.map((card, index) => (
                <Card key={index} className={classes.featureCard} shadow="sm" padding="lg" radius='md' withBorder>
                    <Text fz="xl" fw={700} sx={{ fontFamily: "'Outfit', sans-serif" }}>{card.title}</Text>
                    <Text mt={6}>{card.content}</Text>
                </Card>
            ))}
        </SimpleGrid>
    )

    const DiscoveryButton = () => (
        <>
            <br />
            <Center>
                <Text color="dimmed" size="sm">
                    If you'd like to hear more about the app, we'd love to chat!
                </Text>
            </Center>
            <Space h="sm" />
            <Center>
                <Button
                    color='green'
                    size='md'
                    component='a'
                    href='https://cal.com/pete-pobblebonk-yef5rr/30min'
                    type='button'
                    sx={{
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 24px rgba(0,140,0,0.3)',
                        }
                    }}
                >
                    Book a discovery call
                </Button>
            </Center>
        </>
    )

    return (

        <body>

            <Head />

            {/* ── Hero ── */}
            <div className={classes.heroWrapper}>
                <div className={classes.hero}>
                    <div className={classes.heroImageContainer}>
                        <Image src={twophone} fit='contain' className={classes.heroImage} />
                        <Image src={laptop} fit='contain' className={classes.heroImage} />
                    </div>

                    <div className={classes.heroContent}>
                        <Image src={transparentlogo} fit='contain' maw={200} mx='auto' />
                        <Badge color="grape" variant="filled" size="md" radius="sm" mt="xs">
                            Since 2022
                        </Badge>

                        <Space h='lg' />
                        <Space h='lg' />

                        <Container>
                            <Title className={classes.heroTitle} order={1}>
                                Because The World Needs{' '}
                                <span style={{
                                    color: '#1a9e2e',
                                    WebkitTextFillColor: '#1a9e2e',
                                }}>More</span>{' '}
                                Comedy!
                            </Title>
                            <Text size="xl" weight={600} color="dimmed" align="center" mt="md" sx={{ fontFamily: "'Outfit', sans-serif" }}>
                                Connecting comics, producers, and fans since 2022.
                            </Text>
                        </Container>

                        <Space h='xl' />

                        {/* Web App CTA Actions */}
                        <Group spacing="md" position="center" mb="xl" sx={{ width: '100%' }}>
                            <Button
                                size="xl"
                                color="grape"
                                component="a"
                                href="https://www.comedyseen.com/map"
                                sx={{
                                    fontSize: rem(18),
                                    height: rem(54),
                                    boxShadow: '0 8px 20px rgba(123,0,184,0.25)',
                                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 12px 28px rgba(123,0,184,0.4)',
                                    },
                                    '@media (max-width: 36em)': {
                                        width: '100%',
                                        fontSize: rem(16),
                                    }
                                }}
                            >
                                Go to Open Mic/Showcase Map
                            </Button>
                            <Button
                                size="xl"
                                color="green"
                                component="a"
                                href="https://www.comedyseen.com/upcoming"
                                sx={{
                                    fontSize: rem(18),
                                    height: rem(54),
                                    boxShadow: '0 8px 20px rgba(26,158,46,0.25)',
                                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 12px 28px rgba(26,158,46,0.4)',
                                    },
                                    '@media (max-width: 36em)': {
                                        width: '100%',
                                        fontSize: rem(16),
                                    }
                                }}
                            >
                                Search by City
                            </Button>
                            <Button
                                size="xl"
                                color="blue"
                                component="a"
                                href="https://www.comedyseen.com/leaderboards"
                                sx={{
                                    fontSize: rem(18),
                                    height: rem(54),
                                    boxShadow: '0 8px 20px rgba(34,139,230,0.25)',
                                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 12px 28px rgba(34,139,230,0.4)',
                                    },
                                    '@media (max-width: 36em)': {
                                        width: '100%',
                                        fontSize: rem(16),
                                    }
                                }}
                            >
                                View Leaderboards
                            </Button>
                        </Group>

                        <Space h='xs' />

                        {/* Animated app-store badges */}
                        <div className={classes.badgeRow}>
                            <a
                                href="https://apps.apple.com/us/app/pobblebonk-app/id1606669167"
                                className="badge-animate"
                                style={{ animationDelay: '0s' }}
                            >
                                <Image src={apple} className={classes.badgeImg} fit='contain' />
                            </a>
                            <a
                                href="https://play.google.com/store/apps/details?id=com.pobblebonk.pobblebonk"
                                className="badge-animate"
                                style={{ animationDelay: '0.14s' }}
                            >
                                <Image src={android} className={classes.badgeImg} fit='contain' />
                            </a>
                        </div>


                    </div>
                </div>
            </div>

            {/* ── Divider: Hero → Testimonials ── */}
            <Divider
                className={classes.sectionDivider}
                label={<span className={classes.dividerLabel}>What people are saying</span>}
                labelPosition="center"
                size="xs"
            />

            {/* ── Testimonials ── */}
            <Box px={20}>
                <TestimonialsCarousel testimonials={testimonial_data} />
            </Box>

            {/* ── Divider: Testimonials → Features ── */}
            <Divider
                className={classes.sectionDivider}
                label={<span className={classes.dividerLabel}>What's in it for you?</span>}
                labelPosition="center"
                size="xs"
            />

            {/* ── Feature Tabs ── */}
            <Box className={classes.tabsBox}>
                <Tabs defaultValue="Fans" color="green" variant='pills'>
                    <Tabs.List position='center'>
                        <Tabs.Tab
                            sx={{ fontSize: '1.15rem', fontFamily: "'Outfit', sans-serif", fontWeight: 600, '@media (max-width: 36em)': { fontSize: '0.9rem' } }}
                            value="Fans"
                        >Fans</Tabs.Tab>
                        <Tabs.Tab
                            sx={{ fontSize: '1.15rem', fontFamily: "'Outfit', sans-serif", fontWeight: 600, '@media (max-width: 36em)': { fontSize: '0.9rem' } }}
                            value="Comedians"
                        >Comedians</Tabs.Tab>
                        <Tabs.Tab
                            sx={{ fontSize: '1.15rem', fontFamily: "'Outfit', sans-serif", fontWeight: 600, '@media (max-width: 36em)': { fontSize: '0.9rem' } }}
                            value="Producers"
                        >Producers</Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value="Fans" pt="xs">
                        <Space h="md" />
                        {renderCards(fan_cards)}
                        <DiscoveryButton />
                    </Tabs.Panel>

                    <Tabs.Panel value="Comedians" pt="xs">
                        <Space h="md" />
                        {renderCards(comedian_cards)}
                        <DiscoveryButton />
                    </Tabs.Panel>

                    <Tabs.Panel value="Producers" pt="xs">
                        <Space h="md" />
                        {renderCards(producer_cards)}
                        <DiscoveryButton />
                    </Tabs.Panel>

                </Tabs>
            </Box>

            <Footer links={links} />

        </body>
    )
}

export default Landing